// Unified cart surface. For guests: reads/writes are local (Zustand).
// For authenticated users: server-state via TanStack Query.
//
// Mutations always invalidate the server cart query so subsequent reads
// reflect the latest server-computed totals (including taxes/discounts).
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as cartSvc from '@core/services/cartService';
import { useAuthStore } from '@core/store/authStore';
import { useGuestCartStore } from '@core/store/guestCartStore';
import { selectPlatformSlug, useStoreContextStore } from '@core/store/storeContextStore';

const CART_KEY = 'cart';

function emptyCartFrom(cart) {
  return {
    ...(cart ?? {}),
    items: [],
    subtotal: 0,
    taxTotal: 0,
    discountAmount: 0,
    deliveryCharge: 0,
    chargesBreakdown: [],
    grandTotal: 0,
  };
}

function removeCartLineFromCache(lineId) {
  return (cart) => {
    if (!cart?.items) return cart;

    const items = cart.items.filter((item) => item.lineId !== lineId);
    if (items.length === 0) return emptyCartFrom(cart);

    return {
      ...cart,
      items,
    };
  };
}

function useIsAuthed() {
  return useAuthStore((s) => Boolean(s.accessToken && s.customer));
}

export function useActiveCart({ query } = {}) {
  const isAuthed = useIsAuthed();
  const accountId = useStoreContextStore((s) => s.accountId);
  const locationId = useStoreContextStore((s) => s.locationId);
  const platformSlug = useStoreContextStore(selectPlatformSlug);
  const guestLines = useGuestCartStore((s) => s.lines);
  const sessionId = useGuestCartStore((s) => s.sessionId);

  const serverQuery = useQuery({
    queryKey: [
      CART_KEY,
      'active',
      { accountId, locationId, platformSlug, sessionId, isAuthed, query },
    ],
    queryFn: () =>
      cartSvc.getActiveCart({
        sessionId,
        preferApiKey: !isAuthed,
        query,
      }),
    enabled: Boolean(locationId && sessionId),
    placeholderData: keepPreviousData,
  });

  if (serverQuery.data) {
    const cart =
      serverQuery.data?.items?.length === 0 ? emptyCartFrom(serverQuery.data) : serverQuery.data;

    return {
      cart,
      isLoading: serverQuery.isLoading,
      isFetching: serverQuery.isFetching,
      isPlaceholderData: serverQuery.isPlaceholderData,
      isError: serverQuery.isError,
      error: serverQuery.error,
      lineCount: cart?.items?.reduce((s, l) => s + (l.quantity ?? 0), 0) ?? 0,
      subtotal: cart?.subtotal ?? 0,
      isGuest: !isAuthed,
    };
  }

  const guestSubtotal = guestLines.reduce(
    (sum, line) => sum + line.unitPrice * line.quantity + (line.modifierTotal ?? 0),
    0,
  );
  const count = guestLines.reduce((s, l) => s + l.quantity, 0);

  return {
    cart: {
      cartId: 'guest',
      status: 'active',
      items: guestLines.map((l) => ({
        lineId: l.lineId,
        itemId: l.itemId,
        itemName: l.itemName,
        quantity: l.quantity,
        basePrice: l.unitPrice,
        mrp: l.mrp ?? l.unitPrice,
        lineSubtotal: (l.mrp ?? l.unitPrice) * l.quantity,
        lineTotal: l.unitPrice * l.quantity + (l.modifierTotal ?? 0),
        imageUrl: l.imageUrl,
        uom: l.uom,
        selectedUomId: l.selectedUomId,
        conversionRate: l.conversionRate,
        alternativeUnit: l.alternativeUnit,
        alternativeUnitDetails: l.alternativeUnitDetails ?? l.alternativeUnitConversion,
        variant: l.variant,
        selectedVariants: l.selectedVariants,
        selectedModifiers: l.selectedModifiers,
        modifiers: l.modifiers,
        comboSelections: l.comboSelections,
        selectedComboItems: l.selectedComboItems,
        removedIngredients: l.removedIngredients,
        serviceItems: l.serviceItems,
        itemVariantSkuId: l.itemVariantSkuId,
      })),
      subtotal: guestSubtotal,
      taxTotal: 0,
      discountAmount: 0,
      deliveryCharge: 0,
      chargesBreakdown: [],
      grandTotal: guestSubtotal,
    },
    isLoading: serverQuery.isLoading || serverQuery.isFetching,
    isFetching: serverQuery.isFetching,
    isPlaceholderData: serverQuery.isPlaceholderData,
    isError: false,
    error: null,
    lineCount: count,
    subtotal: guestSubtotal,
    isGuest: true,
  };
}

export function useAddToCart() {
  const isAuthed = useIsAuthed();
  const queryClient = useQueryClient();
  const guestAdd = useGuestCartStore((s) => s.add);
  const ensureSession = useGuestCartStore((s) => s.ensureSession);

  return useMutation({
    mutationKey: [CART_KEY, 'add'],
    mutationFn: async (input) => {
      const sessionId = ensureSession();
      try {
        await cartSvc.addItem({
          ...input,
          sessionId,
          preferApiKey: !isAuthed,
        });
        return { source: 'server' };
      } catch (error) {
        if (isAuthed) throw error;
      }

      console.info('[cart:addItem] guest cart update (no API call)', input);
      guestAdd({
        itemId: input.itemId,
        itemName: input.itemName ?? 'Item',
        unitPrice: input.unitPrice ?? 0,
        mrp: input.mrp ?? input.unitPrice ?? 0,
        quantity: input.quantity ?? 1,
        imageUrl: input.imageUrl,
        uom: input.uom,
        selectedUomId: input.selectedUomId,
        conversionRate: input.conversionRate,
        alternativeUnit: input.alternativeUnit,
        alternativeUnitConversion: input.alternativeUnitConversion,
        alternativeUnitDetails: input.alternativeUnitConversion,
        variant: input.variant,
        modifiers: input.modifiers,
        selectedVariants: input.selectedVariants,
        selectedModifiers: input.selectedModifiers,
        modifierTotal: input.modExtra ?? input.modifierTotal ?? 0,
        comboSelections: input.comboSelections,
        selectedComboItems: input.selectedComboItems,
        removedIngredients: input.removedIngredients,
        serviceItems: input.serviceItems,
        itemVariantSkuId: input.itemVariantSkuId,
      });
      return { source: 'guest' };
    },
    onSuccess: (result) => {
      if (result.source === 'server') {
        queryClient.invalidateQueries({ queryKey: [CART_KEY, 'active'] });
      }
    },
  });
}

export function useUpdateCartLine() {
  const isAuthed = useIsAuthed();
  const queryClient = useQueryClient();
  const guestRemove = useGuestCartStore((s) => s.remove);
  const guestAdd = useGuestCartStore((s) => s.add);
  const guestUpdate = useGuestCartStore((s) => s.updateQuantity);
  const ensureSession = useGuestCartStore((s) => s.ensureSession);

  return useMutation({
    mutationKey: [CART_KEY, 'update'],
    mutationFn: async ({
      lineId,
      quantity,
      itemId,
      itemName,
      unitPrice,
      mrp,
      imageUrl,
      uom,
      uomId,
      unit,
      unitId,
      selectedUomId,
      conversionRate,
      alternativeUnit,
      alternativeUnitConversion,
      variant,
      modifiers,
      selectedVariants,
      selectedModifiers,
      modExtra,
      modifierTotal,
      comboSelections,
      selectedComboItems,
      removedIngredients,
      serviceItems,
      notes,
      itemVariantSkuId,
      skuLocationId,
    }) => {
      const sessionId = ensureSession();
      try {
        await cartSvc.updateLine(lineId, {
          notes,
          quantity,
          variant,
          modifiers,
          selectedVariants,
          selectedModifiers,
          uom,
          uomId,
          unit,
          unitId,
          selectedUomId,
          conversionRate,
          alternativeUnit,
          alternativeUnitConversion,
          comboSelections,
          selectedComboItems,
          removedIngredients,
          serviceItems,
          itemId,
          itemVariantSkuId,
          skuLocationId,
          sessionId,
          preferApiKey: !isAuthed,
        });
      } catch (error) {
        if (isAuthed) throw error;
        if (
          selectedVariants ||
          selectedModifiers ||
          variant ||
          modifiers ||
          modExtra ||
          modifierTotal ||
          comboSelections ||
          selectedComboItems ||
          removedIngredients ||
          serviceItems
        ) {
          const currentLine = useGuestCartStore
            .getState()
            .lines.find((line) => line.lineId === lineId);
          if (currentLine) {
            guestRemove(lineId);
            guestAdd({
              itemId: itemId ?? currentLine.itemId,
              itemName: itemName ?? currentLine.itemName,
              unitPrice: unitPrice ?? currentLine.unitPrice,
              mrp: mrp ?? currentLine.mrp ?? unitPrice ?? currentLine.unitPrice,
              quantity: quantity ?? currentLine.quantity,
              imageUrl: imageUrl ?? currentLine.imageUrl,
              uom: uom ?? currentLine.uom,
              selectedUomId: selectedUomId ?? currentLine.selectedUomId,
              conversionRate: conversionRate ?? currentLine.conversionRate,
              alternativeUnit: alternativeUnit ?? currentLine.alternativeUnit,
              alternativeUnitConversion:
                alternativeUnitConversion ?? currentLine.alternativeUnitConversion,
              alternativeUnitDetails:
                alternativeUnitConversion ?? currentLine.alternativeUnitDetails,
              variant: variant ?? currentLine.variant,
              modifiers: modifiers ?? currentLine.modifiers,
              selectedVariants: selectedVariants ?? currentLine.selectedVariants,
              selectedModifiers: selectedModifiers ?? currentLine.selectedModifiers,
              modifierTotal: modifierTotal ?? modExtra ?? currentLine.modifierTotal ?? 0,
              comboSelections: comboSelections ?? currentLine.comboSelections,
              selectedComboItems: selectedComboItems ?? currentLine.selectedComboItems,
              removedIngredients: removedIngredients ?? currentLine.removedIngredients,
              serviceItems: serviceItems ?? currentLine.serviceItems,
              itemVariantSkuId: itemVariantSkuId ?? currentLine.itemVariantSkuId,
            });
            return;
          }
        }
        guestUpdate(lineId, quantity);
      }
    },
    onSuccess: (_, variables) => {
      if (variables?.quantity <= 0) {
        queryClient.setQueriesData(
          { queryKey: [CART_KEY, 'active'] },
          removeCartLineFromCache(variables.lineId),
        );
      }
      queryClient.invalidateQueries({ queryKey: [CART_KEY, 'active'] });
    },
  });
}

export function useRemoveCartLine() {
  const isAuthed = useIsAuthed();
  const queryClient = useQueryClient();
  const guestRemove = useGuestCartStore((s) => s.remove);
  const ensureSession = useGuestCartStore((s) => s.ensureSession);

  return useMutation({
    mutationKey: [CART_KEY, 'remove'],
    mutationFn: async (lineId) => {
      const sessionId = ensureSession();
      try {
        await cartSvc.removeLine(lineId, {
          sessionId,
          preferApiKey: !isAuthed,
        });
      } catch (error) {
        if (isAuthed) throw error;
        guestRemove(lineId);
      }
    },
    onSuccess: (_, lineId) => {
      queryClient.setQueriesData(
        { queryKey: [CART_KEY, 'active'] },
        removeCartLineFromCache(lineId),
      );
      queryClient.invalidateQueries({ queryKey: [CART_KEY, 'active'] });
    },
  });
}

export function useClearCart() {
  const isAuthed = useIsAuthed();
  const queryClient = useQueryClient();
  const guestClear = useGuestCartStore((s) => s.clear);
  const locationId = useStoreContextStore((s) => s.locationId);
  const accountId = useStoreContextStore((s) => s.accountId);
  const ensureSession = useGuestCartStore((s) => s.ensureSession);

  return useMutation({
    mutationKey: [CART_KEY, 'clear', { locationId }],
    mutationFn: async () => {
      const sessionId = ensureSession();
      try {
        let activeCart = queryClient.getQueryData([
          CART_KEY,
          'active',
          { accountId, locationId, sessionId, isAuthed, query: undefined },
        ]);
        if (!activeCart) {
          activeCart = await cartSvc.getActiveCart({ sessionId, preferApiKey: !isAuthed });
        }
        const lineIds = activeCart?.items?.map((item) => item.lineId) ?? [];
        if (lineIds.length === 0) return;
        await cartSvc.clearCart(lineIds, { sessionId, preferApiKey: !isAuthed });
      } catch (error) {
        if (isAuthed) throw error;
        guestClear();
      }
    },
    onSuccess: () => {
      guestClear();
      queryClient.setQueriesData({ queryKey: [CART_KEY, 'active'] }, emptyCartFrom);
      queryClient.invalidateQueries({ queryKey: [CART_KEY, 'active'] });
    },
  });
}

export function useCheckout() {
  return useMutation({
    mutationKey: [CART_KEY, 'checkout'],
    mutationFn: cartSvc.checkout,
  });
}
